import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import categoryService from '../../services/categoryService';

const CategoriesPanel = ({ settings, categories, refreshCategories }) => {
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: '#808080'
  });

  // Generic form change handler
  const handleCategoryChange = (field) => (event) => {
    setCategoryForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Category operations
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      color: '#808080'
    });
    setShowCategoryForm(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category._id);
    setCategoryForm({
      name: category.name,
      color: category.color
    });
    setShowCategoryForm(true);
  };

  const handleCancelCategory = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) {
      alert('Kategori adı gereklidir');
      return;
    }

    try {
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory, categoryForm);
      } else {
        await categoryService.createCategory(settings._id, {
          ...categoryForm,
          timeline_id: settings._id
        });
      }

      setShowCategoryForm(false);
      await refreshCategories();
      
      // Dispatch custom event to notify timeline of category changes
      document.dispatchEvent(new CustomEvent('timelineDataUpdated'));
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Kategori kaydedilirken bir hata oluştu');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await categoryService.deleteCategory(categoryId);
      await refreshCategories();
      
      // Dispatch custom event to notify timeline of category changes
      document.dispatchEvent(new CustomEvent('timelineDataUpdated'));
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Kategori silinirken bir hata oluştu');
    }
  };

  return (
    <>
      {/* Button to add new category or form heading */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          {showCategoryForm ? 
            (editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori Ekle') : 
            'Kategoriler'}
        </Typography>
        {!showCategoryForm && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleAddCategory}
            startIcon={<EditIcon />}
          >
            Kategori Ekle
          </Button>
        )}
      </Box>
      
      {/* Category form - shown only when adding or editing */}
      {showCategoryForm ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Kategori Adı"
              fullWidth
              margin="normal"
              value={categoryForm.name}
              onChange={handleCategoryChange('name')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Renk"
              fullWidth
              margin="normal"
              type="color"
              value={categoryForm.color}
              onChange={handleCategoryChange('color')}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleCancelCategory}
                sx={{ mr: 1 }}
              >
                İptal
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSaveCategory}
              >
                {editingCategory ? 'Kategoriyi Güncelle' : 'Kategori Ekle'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      ) : (
        /* List of existing categories - shown when not adding/editing */
        <Box>
          {categories.length === 0 ? (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              Henüz kategori eklenmemiş
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              {categories.map((category) => (
                <Box 
                  key={category._id} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 2,
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    boxShadow: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box 
                      sx={{ 
                        width: 20, 
                        height: 20, 
                        borderRadius: '50%', 
                        bgcolor: category.color,
                        mr: 2
                      }} 
                    />
                    <Typography>{category.name}</Typography>
                  </Box>
                  <Box>
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditCategory(category)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteCategory(category._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default CategoriesPanel;